import { FC, ReactNode, useEffect, useMemo, useState, useCallback } from 'react';

import classNames from 'classnames';

import { useAppSelector } from '../../../hooks/redux';
import { selectUser } from '../../../store/auth.slice';
import { withHoverIcon } from '../../../hoc/withHoverIcon';
import { CloseCircleCrossBtn } from '../../icons/close-circle-cross-btn';

import ModalWrap from '../../common/modal-wrap';
import Button from '../../common/button';
import { CloseCrossBtn } from '../../icons/close-cross-btn';
import {
  FEATURE_FLAG_TOOL_COLOR_CODE,
  FEATURE_FLAG_TOOL_CLOUDS,
  FEATURE_FLAG_TOOL_GROW_AI,
  FEATURE_FLAG_TOOL_SAND_THERAPY,
} from '../../../constants/featureFlags';
import { useFeatureFlagEnabled } from '../../../hooks/useFeatureFlagEnabled';
import { useTariffToolCheck } from '../../../hooks/useTariffToolCheck';

import { ButtonTool } from './ButtonTool';
import { PsyToolsId, toolNamesMap } from './types';

const CloseCircleCrossBtnHover = withHoverIcon(CloseCircleCrossBtn);

const confirmableTools = new Set<PsyToolsId>([
  PsyToolsId.MAC_CARDS,
  PsyToolsId.DPDG,
  PsyToolsId.EMOTIONS,
  PsyToolsId.WHEEL,
  PsyToolsId.COLOR_CODE,
  PsyToolsId.CLOUDS,
  PsyToolsId.SAND_THERAPY,
]);

const confirmTexts: Record<PsyToolsId, { name: string; description: string }> = {
  [PsyToolsId.EMOTIONS]: {
    name: '«Детектор эмоций»',
    description: 'Клиент перестанет видеть детектор эмоций.',
  },
  [PsyToolsId.DPDG]: {
    name: '«ДПДГ»',
    description: 'Клиент перестанет видеть упражнение.',
  },
  [PsyToolsId.MAC_CARDS]: {
    name: '«МАК карты»',
    description: 'Клиент перестанет видеть карты.',
  },
  [PsyToolsId.QUESTION]: {
    name: '«Новый вопрос»',
    description: '',
  },
  [PsyToolsId.WHEEL]: {
    name: '«Колесо баланса»',
    description: 'Клиент перестанет видеть колесо баланса.',
  },
  [PsyToolsId.COLOR_CODE]: {
    name: '«Цветовой код»',
    description: 'Клиент перестанет видеть цветовой код.',
  },
  [PsyToolsId.CLOUDS]: {
    name: '«Облака»',
    description: 'Клиент перестанет видеть облака.',
  },
  [PsyToolsId.GROW]: {
    name: '«Ai GROW»',
    description: '',
  },
  [PsyToolsId.SAND_THERAPY]: {
    name: '«Песочная терапия»',
    description: 'Клиент перестанет видеть инструмент.',
  },
};
export interface IToolsPanelProps {
  menu?: PsyToolsId;
  setMenu?: (num: PsyToolsId | 0) => void;
  isGrowOpen?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  users: any[];
  panel?: ReactNode;
  closeHidden?: boolean;
  roomName?: string;
}

export const ToolsPanel: FC<IToolsPanelProps> = ({
  menu,
  setMenu,
  isGrowOpen = false,
  users,
  panel,
  closeHidden,
  roomName,
}) => {
  const userData = useAppSelector(selectUser);
  const tariff = useAppSelector((s) => s.tariffSlice);

  const psy_tools = userData?.active_psy_tools;
  const noClientConnected = users.length <= 1;
  const shown_tools = psy_tools;
  const [pendingCloseTool, setPendingCloseTool] = useState<PsyToolsId | null>(null);
  const activeTool = useAppSelector((state) => state.activeToolStateSlice.activeTool);
  const isConfirmCloseOpen = pendingCloseTool !== null;
  const isColorCodeEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_COLOR_CODE);
  const isCloudsEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_CLOUDS);
  const isSandTherapyEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_SAND_THERAPY);
  const isGrowAiEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_GROW_AI);
  const canUseEmotions = useTariffToolCheck(toolNamesMap[PsyToolsId.EMOTIONS]);
  const canUseDpdg = useTariffToolCheck(toolNamesMap[PsyToolsId.DPDG]);
  const canUseMacCards = useTariffToolCheck(toolNamesMap[PsyToolsId.MAC_CARDS]);
  const canUseQuestion = useTariffToolCheck(toolNamesMap[PsyToolsId.QUESTION]);
  const canUseWheel = useTariffToolCheck(toolNamesMap[PsyToolsId.WHEEL]);
  const canUseColorCode = useTariffToolCheck(toolNamesMap[PsyToolsId.COLOR_CODE]);
  const canUseClouds = useTariffToolCheck(toolNamesMap[PsyToolsId.CLOUDS]);
  const canUseSandTherapy = useTariffToolCheck(toolNamesMap[PsyToolsId.SAND_THERAPY]);
  const canUseToolByName = useMemo(
    () => ({
      [toolNamesMap[PsyToolsId.EMOTIONS]]: canUseEmotions,
      [toolNamesMap[PsyToolsId.DPDG]]: canUseDpdg,
      [toolNamesMap[PsyToolsId.MAC_CARDS]]: canUseMacCards,
      [toolNamesMap[PsyToolsId.QUESTION]]: canUseQuestion,
      [toolNamesMap[PsyToolsId.WHEEL]]: canUseWheel,
      [toolNamesMap[PsyToolsId.COLOR_CODE]]: canUseColorCode,
      [toolNamesMap[PsyToolsId.CLOUDS]]: canUseClouds,
      [toolNamesMap[PsyToolsId.SAND_THERAPY]]: canUseSandTherapy,
    }),
    [canUseClouds, canUseColorCode, canUseDpdg, canUseEmotions, canUseMacCards, canUseQuestion, canUseSandTherapy, canUseWheel],
  );

  const onSetMenu = useCallback(
    (num: PsyToolsId | 0) => {
      if (!setMenu) {
        return;
      }

      const nextValue = num === menu ? 0 : num;
      setMenu(nextValue);
    },
    [menu, setMenu],
  );

  useEffect(() => {
    if (pendingCloseTool && activeTool !== pendingCloseTool) {
      setPendingCloseTool(null);
    }
  }, [activeTool, pendingCloseTool]);

  const handlePanelClose = () => {
    if (menu && confirmableTools.has(menu as PsyToolsId) && activeTool === menu) {
      setPendingCloseTool(menu as PsyToolsId);
      return;
    }

    onSetMenu(0);
  };

  const confirmCloseTool = () => {
    onSetMenu(0);
    setPendingCloseTool(null);
  };

  const cancelCloseTool = () => {
    setPendingCloseTool(null);
  };

  const confirmData = pendingCloseTool ? confirmTexts[pendingCloseTool] : null;
  const confirmMessage = confirmData
    ? `Вы действительно хотите закрыть инструмент ${confirmData.name}?${confirmData.description ? ` ${confirmData.description}` : ''}`
    : '';

  const colorProps = useMemo(() => {
    return {
      defaultFill: '#828282',
      defaultFillBg: 'rgba(255, 255, 255, 0.8)',
      hoverFill: '#63656A',
      hoverFillBg: '#DEC05E',
    };
  }, []);

  const iconButtonSet = useMemo(() => {
    return Object.values(PsyToolsId)
      .filter((v) => typeof v === 'number')
      .filter((v) => {
        const tool = toolNamesMap[v];
        if (tool === 'grow_ai' && !isGrowAiEnabled) return false;
        if (tool === 'color_code' && !isColorCodeEnabled) return false;
        if (tool === 'clouds' && !isCloudsEnabled) return false;
        if (tool === 'sand_therapy' && !isSandTherapyEnabled) return false;

        if (canUseToolByName[tool]) {
          return shown_tools?.includes(tool);
        }

        return true; //show if tool disabled by tariff
      })
      .map((toolId, index) => {
        const key = `bt-${index}-${toolId ?? 0}`;

        return (
          <ButtonTool
            key={key}
            selected={toolId === PsyToolsId.GROW ? isGrowOpen : menu === toolId}
            thisMenuId={toolId}
            disabled={noClientConnected}
            onSetMenu={onSetMenu}
          />
        );
      });
  }, [
    menu,
    isGrowOpen,
    noClientConnected,
    onSetMenu,
    shown_tools,
    tariff,
    canUseToolByName,
    isColorCodeEnabled,
    isCloudsEnabled,
    isSandTherapyEnabled,
    isGrowAiEnabled,
  ]);

  return (
    <>
      <div
        className={classNames(
          'w-full h-full flex flex-row grow bg-[#63656A] sm:rounded-[5px] md:rounded-[15px] gap-0 md:basis-0',
        )}
      >
        <div
          className={classNames('h-full flex flex-col items-center gap-[1rem] justify-start m-[0.5rem]', {
            'w-[48px]': !psy_tools || psy_tools.length <= 0,
          })}
        >
          {iconButtonSet}
        </div>

        {panel && ( //border-double border-[#DEC05E] border-solid border-1
          <div className="relative w-full h-[99%] items-start grow bg-[#63656A] sm:rounded-[5px] md:rounded-[15px] ">
            {panel}
            {!closeHidden && (
              <button
                onClick={handlePanelClose}
                className="absolute top-5 right-5 cursor-default"
                aria-label="Close panel"
              >
                <CloseCircleCrossBtnHover className="" {...colorProps} />
              </button>
            )}
          </div>
        )}
      </div>

      <ModalWrap isShow={isConfirmCloseOpen}>
        <div className="think-scroll flex flex-col gap-6 w-full max-w-[90%] sm:max-w-[60%] md:max-w-[560px] h-auto rounded-[20px]  p-6 lg:p-10 bg-white shadow-shadowPopup">
          <div className="w-full flex justify-between items-start">
            <h2 className="font-comfortaa text-lg lg:text-2xl text-[#111026] font-bold">Закрытие инструмента</h2>
            <button className="cursor-pointer" onClick={cancelCloseTool} aria-label="Отменить закрытие">
              <CloseCrossBtn width={20} height={20} />
            </button>
          </div>
          <p className="text-sm md:text-base font-bold text-[#585858]">{confirmMessage}</p>
          <div className="flex justify-end gap-4">
            <Button type="grey" className="!w-max px-4 py-2 !text-sm md:!text-base" onClick={cancelCloseTool}>
              Оставить открытым
            </Button>
            <Button type="black" className="!w-max px-4 py-2 !text-sm md:!text-base" onClick={confirmCloseTool}>
              Закрыть инструмент
            </Button>
          </div>
        </div>
      </ModalWrap>
    </>
  );
};
